export interface CaseShortSceneRef {
  id?: string;
  sceneIds?: string[];
}

export interface CaseShortStoryboardScene {
  scene_id?: string;
  sceneId?: string;
  assetUrl?: string | null;
  assetId?: string | null;
  imageJob?: {
    status?: string;
    attempt?: number;
    error?: string;
    completedAt?: string;
    forceRefreshShortId?: string;
  };
}

export interface CaseShortPublicationState {
  caseId: string;
  shortId: string;
  status: string;
  platforms?: { scheduleStatus?: string | null }[];
}

export function shortRenderHasRenderer(status?: string | null) {
  return ['CLAIMED', 'PREPARING', 'RENDERING', 'UPLOADING'].includes(status ?? '');
}

export function caseShortAlreadyScheduled(caseId: string, shortId: string, publications: CaseShortPublicationState[]) {
  const publication = publications.find((row) => row.caseId === caseId && row.shortId === shortId);
  return Boolean(publication && (['QUEUED', 'PUBLISHED'].includes(publication.status) || publication.platforms?.some((platform) => ['PENDING', 'SENT'].includes(String(platform.scheduleStatus ?? '')))));
}

export function getCaseShortVisualProgress(short: CaseShortSceneRef, storyboard: CaseShortStoryboardScene[]) {
  const ids = short.sceneIds ?? [];
  const scenes = ids.map((id) => storyboard.find((scene, index) => String(scene.scene_id ?? scene.sceneId ?? String(index + 1).padStart(2, '0')) === id)).filter((scene): scene is CaseShortStoryboardScene => Boolean(scene));
  const requestedRefreshScenes = scenes.filter((scene) => Boolean(short.id && scene.imageJob?.forceRefreshShortId === short.id));
  const recoveryScenes = requestedRefreshScenes.length ? requestedRefreshScenes : scenes.filter((scene) => Number(scene.imageJob?.attempt ?? 0) > 0);
  const active = scenes.filter((scene) => ['QUEUED', 'PROCESSING'].includes(String(scene.imageJob?.status ?? '')));
  const failed = scenes.filter((scene) => scene.imageJob?.status === 'FAILED');
  const ready = scenes.filter((scene) => Boolean(scene.assetUrl || scene.assetId));
  const recoveredVisualsPending = recoveryScenes.some((scene) => scene.imageJob?.forceRefreshShortId === short.id
    ? ['QUEUED', 'PROCESSING'].includes(String(scene.imageJob?.status ?? ''))
    : !scene.assetUrl && !scene.assetId && ['QUEUED', 'PROCESSING'].includes(String(scene.imageJob?.status ?? '')));
  const recoveredVisualsReady = recoveryScenes.length > 0 && recoveryScenes.every((scene) => Boolean(scene.assetUrl || scene.assetId)
    && (scene.imageJob?.forceRefreshShortId !== short.id || scene.imageJob?.status === 'COMPLETED'));
  return {
    total: scenes.length,
    ready: ready.length,
    queued: active.filter((scene) => scene.imageJob?.status === 'QUEUED').length,
    processing: active.filter((scene) => scene.imageJob?.status === 'PROCESSING').length,
    failed: failed.length,
    recovering: recoveryScenes.length > 0,
    recoveredVisualsPending,
    recoveredVisualsReady,
    active: active.length > 0,
    completedAt: recoveryScenes.map((scene) => scene.imageJob?.completedAt).filter((value): value is string => Boolean(value)).sort().at(-1) ?? null,
  };
}

export function replacementRenderNeeded(progress: ReturnType<typeof getCaseShortVisualProgress>, renderCreatedAt?: string | null) {
  if (!progress.recoveredVisualsReady || !progress.completedAt) return false;
  const renderTime = Date.parse(String(renderCreatedAt ?? ''));
  const imageTime = Date.parse(progress.completedAt);
  return !Number.isFinite(renderTime) || renderTime < imageTime;
}
